package co.uk.paxes.pinpoint.web

import kotlinx.browser.document
import kotlinx.browser.window
import org.w3c.dom.*
import kotlin.js.json
import kotlin.js.Date
import kotlin.random.Random

/**
 * Data class representing room information for display.
 * Requirements: 2.1, 2.2
 */
data class RoomInfo(
    val id: String,
    val name: String,
    val playerCount: Int,
    val maxPlayers: Int,
    val gameStarted: Boolean
) {
    val isWaiting: Boolean get() = !gameStarted
    val isFull: Boolean get() = playerCount >= maxPlayers
}

/**
 * Data class representing player information in a room.
 * Requirements: 1.1, 1.3
 * Updated to match Android PlayerInfo structure for AI compatibility
 */
data class PlayerInfo(
    val id: String,
    val name: String,
    val host: Boolean,
    val connected: Boolean,
    val platform: String = "web",
    val isAI: Boolean = false,           // AI player flag (matches Android)
    val aiDifficulty: String? = null     // AI difficulty: EASY, MEDIUM, HARD, EXPERT (matches Android)
)

/**
 * Data class representing room state from Firebase.
 * Requirements: 1.2, 6.1
 */
data class RoomState(
    val id: String,
    val name: String,
    val players: Map<String, PlayerInfo>,
    val maxPlayers: Int,
    val gameStarted: Boolean,
    val gameInfo: GameInfo?
)

/**
 * Data class representing game info when a game is started.
 * Requirements: 6.3
 */
data class GameInfo(
    val gameId: String,
    val boardSeed: Long,
    val currentPlayerId: String?
)

/**
 * FirebaseRoomManager handles room operations: join, leave, list rooms, start game.
 * Implements the interface defined in the design document.
 * 
 * Requirements: 1.1, 1.2, 1.3, 1.5, 2.1, 2.2, 2.3, 2.4, 6.1, 6.2, 6.3
 */
object FirebaseRoomManager {
    // External Firebase reference (set from Main.kt)
    var database: dynamic = null
    
    // Current room state
    /**
     * Forcefully reset a room by clearing its players and game state.
     * Useful for clearing stubborn "ghost" players.
     */
    /** True when running inside a sandboxed third-party iframe (Playgama, itch.io, etc.).
     *  Firebase WebSocket is silently blocked in these contexts; REST must be used instead. */
    private val isThirdPartyIframe: Boolean get() {
        val isInIframe = js("window.self !== window.top") as Boolean
        val isOnOwnHosting = js("window.location.hostname.indexOf('pinpoint.click') !== -1 || window.location.hostname === 'localhost'") as Boolean
        return isInIframe && !isOnOwnHosting
    }

    fun forceResetRoom(roomId: String, callback: () -> Unit) {
        val roomRef = database.ref("fixed-rooms/$roomId")
        
        // 1. Reset game state
        val updates = json(
            "gameStarted" to false,
            "gameInfo" to null,
            "lastActivity" to Date.now()
        )
        
        roomRef.update(updates, fun(error: dynamic): dynamic {
            roomRef.child("players").remove(fun(error2: dynamic): dynamic {
                console.log("Room $roomId forcefully reset")
                callback()
                return Unit.asDynamic()
            })
            return Unit.asDynamic()
        })
    }

    private var currentRoomId: String? = null
    private var heartbeatIntervalId: Int? = null
    var currentPlayerId: String? = null
    var currentPlayerName: String? = null
    private var isHost: Boolean = false
    private var pollIntervalId: Int? = null
    private var lastRoomState: RoomState? = null
    
    // Firebase listeners
    private var roomsListener: dynamic = null
    private var roomListener: dynamic = null
    
    // Callbacks
    var onRoomListUpdated: ((List<RoomInfo>) -> Unit)? = null
    var onRoomStateChanged: ((RoomState) -> Unit)? = null
    var onGameStarted: ((RoomState) -> Unit)? = null
    var onError: ((String) -> Unit)? = null

    // Getters for current state
    fun getCurrentRoomId(): String? = currentRoomId
    fun getCurrentPlayerId(): String? = currentPlayerId
    fun getCurrentPlayerName(): String? = currentPlayerName
    fun isCurrentPlayerHost(): Boolean = isHost
    fun isInRoom(): Boolean = currentRoomId != null

    /**
     * Get or create a persistent player ID for this "machine" (browser session).
     * In PlayGama, uses the PlayGama player ID via bridge API.
     */
    private fun getPersistentPlayerId(): String {
        // CRITICAL FIX: Check for PlayGama player ID first (most reliable)
        val isPlayGama = co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.detectEnvironment() ==
            co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.PLAYGAMA
        
        if (isPlayGama) {
            try {
                val bridge = js("window.bridge || window.playgamaBridge || window.PlaygamaBridge")
                if (bridge != null && bridge != undefined) {
                    val player = bridge.player
                    if (player != null && player != undefined) {
                        val getIDFn = player.getID
                        if (getIDFn != null && getIDFn != undefined) {
                            val result = getIDFn()
                            val id = result?.toString()
                            if (id != null && id.isNotEmpty() && id != "undefined") {
                                console.log("🎮 PlayGama: Got persistent player ID from bridge: $id")
                                return "pgm_$id"  // Prefix with pgm_ to distinguish from web_ IDs
                            }
                        }
                    }
                }
            } catch (e: Exception) {
                console.warn("🎮 PlayGama: Failed to get player ID from bridge: ${e.message}")
            }
        }
        
        // Fallback to localStorage (works for web/standalone/Discord)
        val storedId = window.localStorage.getItem("pinpoint_web_player_id")
        if (storedId != null) return storedId
        
        val newId = "web_${Date.now().toLong()}_${Random.nextInt(999999)}"
        try {
            window.localStorage.setItem("pinpoint_web_player_id", newId)
        } catch (e: Exception) {
            console.warn("Failed to store player ID in localStorage: ${e.message}")
        }
        return newId
    }

    /**
     * Start listening to the room list for real-time updates.
     * Reads from `fixed-rooms` path and parses room data into RoomInfo objects.
     * 
     * Requirements: 2.1, 2.3
     */
    fun startListeningToRooms() {
        console.log("FirebaseRoomManager: startListeningToRooms called")

        val env = co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.getEnvironment()

        // Discord always uses REST polling (blocked by CSP)
        if (env == co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.DISCORD) {
            startRoomsPollingViaRest()
            return
        }

        // Detect third-party iframe (e.g. Playgama, itch.io): game is in an iframe
        // but NOT on Firebase Hosting. Firebase WebSocket is silently blocked in sandboxed iframes.
        val isInIframe = js("window.self !== window.top") as Boolean
        val isOnFirebaseHosting = js("window.location.hostname.indexOf('pinpoint.click') !== -1 || window.location.hostname === 'localhost'") as Boolean
        val isThirdPartyIframe = isInIframe && !isOnFirebaseHosting

        if (isThirdPartyIframe) {
            console.log("🌐 Third-party iframe detected (e.g. Playgama) - using REST polling for rooms")
            startRoomsPollingViaRest()
            return
        }

        if (database == null) {
            console.warn("FirebaseRoomManager: Database not initialized, falling back to REST polling")
            startRoomsPollingViaRest()
            return
        }
        
        // Remove existing listener if any
        stopListeningToRooms()
        
        console.log("FirebaseRoomManager: Setting up Firebase SDK listener on fixed-rooms")
        roomsListener = database.ref("fixed-rooms").on("value", fun(snapshot: dynamic): dynamic {
            val data = snapshot.`val`()
            if (data != null) {
                val rooms = parseRoomList(data)
                onRoomListUpdated?.invoke(rooms)
            } else {
                onRoomListUpdated?.invoke(emptyList())
            }
            return Unit.asDynamic()
        }, fun(error: dynamic): dynamic {
            console.error("FirebaseRoomManager: Error listening to rooms: ${error.message}")
            // Fallback to REST polling if WebSocket connection is blocked by sandbox iframe
            startRoomsPollingViaRest()
            return Unit.asDynamic()
        })
    }

    private fun startRoomsPollingViaRest() {
        console.log("🌐 Sentinel: Starting room list polling via REST")
        pollIntervalId?.let { window.clearInterval(it) }
        
        fetchRoomsViaRest()
        pollIntervalId = window.setInterval({
            fetchRoomsViaRest()
        }, 3000) // Poll rooms every 3 seconds in lobby
    }

    private fun fetchRoomsViaRest() {
        val env = co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.getEnvironment()
        val isDiscord = env == co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.DISCORD
        val isPlayGama = env == co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.PLAYGAMA

        // PlayGama sandbox blocks identitytoolkit.googleapis.com, so skip auth entirely
        // and fetch rooms without a token (requires Firebase rules to allow public read on fixed-rooms)
        if (isPlayGama) {
            val url = "https://pinpoint-1978d-default-rtdb.europe-west1.firebasedatabase.app/fixed-rooms.json"
            console.log("🌐 PlayGama: Fetching rooms without auth token: $url")
            window.fetch(url).then { response ->
                if (response.ok) {
                    response.json().then { data ->
                        val rooms = parseRoomList(data)
                        console.log("🌐 PlayGama: Got ${rooms.size} rooms")
                        onRoomListUpdated?.invoke(rooms)
                    }
                } else {
                    console.warn("🌐 PlayGama: Room fetch failed with status ${response.status}")
                    onRoomListUpdated?.invoke(emptyList())
                }
            }.catch { err ->
                console.error("🌐 PlayGama: Room fetch network error", err)
                onRoomListUpdated?.invoke(emptyList())
            }
            return
        }

        getAuthToken { token ->
            val authParam = if (token != null) "?auth=$token" else ""
            val proxyUrl = if (isDiscord)
                "/firebasedatabase/fixed-rooms.json$authParam"
            else
                "https://pinpoint-1978d-default-rtdb.europe-west1.firebasedatabase.app/fixed-rooms.json$authParam"

            window.fetch(proxyUrl).then { response ->
                if (response.ok) {
                    response.json().then { data ->
                        val rooms = parseRoomList(data)
                        onRoomListUpdated?.invoke(rooms)
                    }
                } else {
                    console.warn("🌐 Sentinel: Room fetch failed with status ${response.status}")
                    onRoomListUpdated?.invoke(emptyList())
                }
            }.catch { err ->
                console.error("🌐 Sentinel: Room fetch network error", err)
                onRoomListUpdated?.invoke(emptyList())
            }
        }
    }


    /**
     * Stop listening to room list updates.
     * Cleans up the Firebase listener.
     */
    fun stopListeningToRooms() {
        pollIntervalId?.let { window.clearInterval(it) }
        pollIntervalId = null
        
        if (roomsListener != null) {
            database?.ref("fixed-rooms")?.off("value", roomsListener)
            roomsListener = null
        }
    }

    /**
     * Parse raw Firebase data into a list of RoomInfo objects.
     * 
     * Requirements: 2.1, 2.2
     */
    private fun parseRoomList(data: dynamic): List<RoomInfo> {
        if (data == null) return emptyList()
        val keys = js("Object.keys")(data) as Array<String>
        return keys.map { id ->
            val room = data[id]
            val name = (room.name as? String) ?: id
            val players = room.players
            val playerCount = if (players != null) {
                (js("Object.keys")(players) as Array<*>).size
            } else {
                0
            }
            val maxPlayers = (room.maxPlayers as? Int) ?: 4
            val gameStarted = (room.gameStarted as? Boolean) ?: false
            
            RoomInfo(
                id = id,
                name = name,
                playerCount = playerCount,
                maxPlayers = maxPlayers,
                gameStarted = gameStarted
            )
        }
    }

    /**
     * Get the list of rooms once (non-realtime).
     * 
     * Requirements: 2.1
     */
    fun listRooms(callback: (List<RoomInfo>) -> Unit) {
        if (database == null) {
            callback(emptyList())
            return
        }
        
        database.ref("fixed-rooms").once("value", fun(snapshot: dynamic): dynamic {
            val data = snapshot.`val`()
            val rooms = if (data != null) parseRoomList(data) else emptyList()
            callback(rooms)
            return Unit.asDynamic()
        })
    }

    /**
     * Join a room with the given player name.
     * Writes player info to `fixed-rooms/{roomId}/players/{playerId}`.
     * Handles host assignment for first player.
     * 
     * Requirements: 1.1, 1.2, 6.1
     */
    fun joinRoom(roomId: String, playerName: String, callback: (Boolean, String?) -> Unit) {
        val env = co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.getEnvironment()
        if (env == co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.DISCORD || isThirdPartyIframe) {
            console.log("🌐 joinRoom: using REST (Discord/iframe mode)")
            joinRoomViaRest(roomId, playerName, callback)
            return
        }

        if (database == null) {
            callback(false, "Not connected to Firebase")
            return
        }
        
        if (playerName.isBlank()) {
            callback(false, "Please enter your name")
            return
        }
        
        // First check room state
        database.ref("fixed-rooms/$roomId").once("value", fun(snapshot: dynamic): dynamic {
            val roomData = snapshot.`val`()
            if (roomData == null) {
                callback(false, "Room not found")
                return Unit.asDynamic()
            }
            
            val players = roomData.players ?: js("{}")
            val playerKeys = js("Object.keys")(players) as Array<String>
            
            // Get persistent "machine" ID
            val myId = getPersistentPlayerId()
            
            // 2. CRITICAL Cleanup: Remove stale entries
            // - Same name but different ID (the ghosts from refreshes)
            // - Old activity (stale from previous games)
            val currentTime = Date.now().toDouble()
            val fiveMinutesAgo = currentTime - (5 * 60 * 1000)
            
            playerKeys.forEach { key ->
                val p = players[key]
                if (p != null) {
                    val pName = p.name as? String ?: ""
                    val pLastActivity = (p.lastActivity as? Double) ?: 0.0
                    
                    // Remove if:
                    // - Same name but different ID
                    // - OR Inactive for > 5 mins (ghosts from hours ago)
                    if ((pName == playerName && key != myId) || (pLastActivity > 0 && pLastActivity < fiveMinutesAgo)) {
                        console.log("🧹 Removing ghost/stale player: name=$pName, id=$key, age=${(currentTime - pLastActivity)/1000}s")
                        database.ref("fixed-rooms/$roomId/players/$key").remove()
                    }
                }
            }
            
            // 3. Recalculate players after potential removal
            // Filter out players that were just removed or are about to be replaced by `myId`
            val activePlayers = playerKeys.filter { key ->
                val p = players[key]
                val pLast = (p.lastActivity as? Double) ?: 0.0
                p != null && key != myId && (p.name as? String) != playerName && (pLast == 0.0 || pLast >= fiveMinutesAgo)
            }
            
            val playerCount = activePlayers.size
            val maxPlayers = (roomData.maxPlayers as? Int) ?: 4
            
            if (playerCount >= maxPlayers) {
                callback(false, "Room is full")
                return Unit.asDynamic()
            }
            
            // Determine host
            val hasHost = activePlayers.any { key -> 
                val p = players[key]
                p != null && (p.host as? Boolean) == true 
            }
            val shouldBeHost = !hasHost
            
            // Store current state
            this.currentRoomId = roomId
            this.currentPlayerId = myId
            this.currentPlayerName = playerName
            this.isHost = shouldBeHost
            
            // Create player info object
            // CRITICAL: Use raw JavaScript numbers (not Kotlin Long) for Firebase compatibility
            // Kotlin Long in JS becomes an object {u7_1, v7_1} which breaks Android parsing
            val currentTimeNum = currentTime  // Already a Double from Date.now().toDouble()
            val playerInfo = json(
                "id" to myId,
                "name" to playerName,
                "host" to shouldBeHost,
                "connected" to true,
                "joinedAt" to currentTimeNum,
                "guest" to false,
                "platform" to "web",
                "lastActivity" to currentTimeNum
            )
            
            // Success - STOP POLLING ROOMS
            stopListeningToRooms()
            
            // Write player to Firebase
            val playerRef = database.ref("fixed-rooms/$roomId/players/$myId")
            
            // Set up onDisconnect to remove player when they close the tab
            playerRef.onDisconnect().remove()
            
            playerRef.set(playerInfo, fun(error: dynamic): dynamic {
                if (error != null) {
                    // Reset state on error
                    stopAll()
                    callback(false, "Failed to join room: ${error.message}")
                } else {
                    // Start heartbeat
                    startHeartbeat(roomId, myId)
                    // Subscribe to room changes
                    subscribeToRoom(roomId)
                    callback(true, null)
                }
                return Unit.asDynamic()
            })
            
            return Unit.asDynamic()
        })
    }

    private fun joinRoomViaRest(roomId: String, playerName: String, callback: (Boolean, String?) -> Unit) {
        console.log("🌐 Sentinel: Joining room $roomId via REST")
        val myId = getPersistentPlayerId()
        
        getAuthToken { token ->
            val authParam = if (token != null) "?auth=$token" else ""
            val isDiscord = co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.getEnvironment() == co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.DISCORD
            val baseUrl = if (isDiscord) "" else "https://pinpoint-1978d-default-rtdb.europe-west1.firebasedatabase.app"
            val proxyUrl = "${baseUrl}/fixed-rooms/$roomId.json$authParam"
            
            // 1. Fetch current room state
            window.fetch(proxyUrl).then { response ->
                if (!response.ok) throw Exception("Failed to fetch room state")
                response.json()
            }.then { roomData: dynamic ->
                val players = roomData.players ?: js("{}")
                val playerKeys = js("Object.keys")(players) as Array<String>
                
                // Determine host
                val hasHost = playerKeys.any { key -> 
                    val p = players[key]
                    p != null && (p.host as? Boolean) == true 
                }
                val shouldBeHost = !hasHost
                
                // Store local state
                this.currentRoomId = roomId
                this.currentPlayerId = myId
                this.currentPlayerName = playerName
                this.isHost = shouldBeHost
                
                // 2. Prepare player data
                val currentTime = Date.now().toDouble()
                val playerInfo = json(
                    "id" to myId,
                    "name" to playerName,
                    "host" to shouldBeHost,
                    "connected" to true,
                    "joinedAt" to currentTime,
                    "guest" to false,
                    "platform" to "web",
                    "lastActivity" to currentTime
                )
                
                // 3. Write via PUT
                val isDiscord = co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.getEnvironment() == co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.DISCORD
                val baseUrl = if (isDiscord) "" else "https://pinpoint-1978d-default-rtdb.europe-west1.firebasedatabase.app"
                val updateUrl = "${baseUrl}/fixed-rooms/$roomId/players/$myId.json$authParam"
                window.fetch(updateUrl, json(
                    "method" to "PUT",
                    "body" to JSON.stringify(playerInfo)
                ).asDynamic()).then { patchResponse ->
                    if (patchResponse.ok) {
                        console.log("✅ Sentinel: Join successful via REST")
                        stopListeningToRooms()
                        subscribeToRoom(roomId) // Start listening to THIS room
                        callback(true, null)
                    } else {
                        callback(false, "Join failed: HTTP ${patchResponse.status}")
                    }
                }
            }.catch { err ->
                console.error("❌ Sentinel: Join failed: ${err.message}")
                callback(false, err.message)
            }
        }
    }

    /**
     * Subscribe to room value changes for real-time updates.
     * 
     * Requirements: 1.2
     */
    private fun subscribeToRoom(roomId: String) {
        val env = co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.getEnvironment()
        if (env == co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.DISCORD || isThirdPartyIframe) {
            console.log("🌐 subscribeToRoom: using REST polling (Discord/iframe mode)")
            startRoomStatePollingViaRest(roomId)
            return
        }

        // Remove existing listener if any
        unsubscribeFromRoom()
        
        roomListener = database.ref("fixed-rooms/$roomId").on("value", fun(snapshot: dynamic): dynamic {
            val roomData = snapshot.`val`()
            if (roomData != null) {
                // PERIODIC CLEANUP: If we are the host (or just a proactive client),
                // remove players that haven't updated their activity in 5 minutes.
                val players = roomData.players ?: js("{}")
                val playerKeys = js("Object.keys")(players) as Array<String>
                val currentTime = Date.now().toDouble()
                val fiveMinutesAgo = currentTime - (5 * 60 * 1000)
                
                playerKeys.forEach { key ->
                    val p = players[key]
                    if (p != null && key != currentPlayerId) {
                        val pLastActivity = (p.lastActivity as? Double) ?: 0.0
                        // Relaxed Cleanup:
                        // Only remove if it HAS an activity timestamp AND it's old.
                        // If it has NO timestamp (like Android), we leave it alone.
                        if (pLastActivity > 0 && pLastActivity < fiveMinutesAgo) {
                            console.log("🧹 Auto-cleaning stale ghost: $key")
                            database.ref("fixed-rooms/$roomId/players/$key").remove()
                        }
                    }
                }
                
                val roomState = parseRoomState(roomId, roomData)
                lastRoomState = roomState
                
                // Check if game has started
                if (roomState.gameStarted && roomState.gameInfo != null) {
                    onGameStarted?.invoke(roomState)
                }
                
                onRoomStateChanged?.invoke(roomState)
            }
            return Unit.asDynamic()
        })
    }

    private var roomStatePollIntervalId: Int? = null
    private var isGameStartedTriggered = false

    private fun startRoomStatePollingViaRest(roomId: String) {
        console.log("🌐 Sentinel: Starting room state polling via REST for $roomId")
        roomStatePollIntervalId?.let { window.clearInterval(it) }
        isGameStartedTriggered = false
        
        roomStatePollIntervalId = window.setInterval({
            getAuthToken { token ->
                val authParam = if (token != null) "?auth=$token" else ""
                // Determine base URL for REST (Discord uses empty, Playgama uses full URL)
                val isDiscord = co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.getEnvironment() == co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.DISCORD
                val baseUrl = if (isDiscord) "" else "https://pinpoint-1978d-default-rtdb.europe-west1.firebasedatabase.app"
                val proxyUrl = "${baseUrl}/fixed-rooms/$roomId.json$authParam"
                
                window.fetch(proxyUrl).then { response ->
                    if (response.ok) {
                        response.json().then { roomData: dynamic ->
                            if (roomData != null) {
                                val roomState = parseRoomState(roomId, roomData)
                                lastRoomState = roomState
                                
                                // Check if game has started
                                if (roomState.gameStarted && roomState.gameInfo != null && !isGameStartedTriggered) {
                                    console.log("🚀 Sentinel: Game start detected via REST poll!")
                                    isGameStartedTriggered = true
                                    stopListeningToRooms() // Stop all lobby polling
                                    unsubscribeFromRoom() // Stop this polling
                                    onGameStarted?.invoke(roomState)
                                }
                                
                                onRoomStateChanged?.invoke(roomState)
                            }
                        }
                    } else {
                        console.warn("🌐 Sentinel: Room state poll failed with status ${response.status}")
                    }
                }.catch { err ->
                    console.error("🌐 Sentinel: Room state poll network error", err)
                }
            }
        }, 1500) // Poll for lobby updates
    }

    /**
     * Unsubscribe from room value changes.
     */
    private fun unsubscribeFromRoom() {
        val roomId = currentRoomId
        
        // Clear REST polling
        roomStatePollIntervalId?.let { window.clearInterval(it) }
        roomStatePollIntervalId = null
        
        // Clear REST polling (legacy/manual fallback)
        pollIntervalId?.let { window.clearInterval(it) }
        pollIntervalId = null
        
        if (roomListener != null && roomId != null) {
            database?.ref("fixed-rooms/$roomId")?.off("value", roomListener)
        }
        roomListener = null
    }

    /**
     * Parse raw Firebase room data into a RoomState object.
     */
    private fun parseRoomState(roomId: String, data: dynamic): RoomState {
        val name = (data.name as? String) ?: roomId
        val maxPlayers = (data.maxPlayers as? Int) ?: 4
        val gameStarted = (data.gameStarted as? Boolean) ?: false
        
        // Parse players
        val playersData = data.players ?: js("{}")
        val playerKeys = js("Object.keys")(playersData) as Array<String>
        val players = playerKeys.associate { key ->
            val p = playersData[key]
            key to PlayerInfo(
                id = (p.id as? String) ?: key,
                name = (p.name as? String) ?: "Unknown",
                host = (p.host as? Boolean) ?: (p.isHost as? Boolean) ?: false,
                connected = (p.connected as? Boolean) ?: (p.isConnected as? Boolean) ?: true,
                platform = (p.platform as? String) ?: "unknown",
                isAI = (p.ai as? Boolean) ?: (p.isAI as? Boolean) ?: false,  // Robust AI flag parsing
                aiDifficulty = p.aiDifficulty as? String  // Parse AI difficulty
            )
        }
        
        // Parse game info if present
        val gameInfo = if (data.gameInfo != null) {
            val info = data.gameInfo
            GameInfo(
                gameId = (info.gameId as? String) ?: "",
                boardSeed = (info.boardSeed as? Number)?.toLong() ?: 0L,
                currentPlayerId = info.currentPlayerId as? String
            )
        } else {
            null
        }
        
        return RoomState(
            id = roomId,
            name = name,
            players = players,
            maxPlayers = maxPlayers,
            gameStarted = gameStarted,
            gameInfo = gameInfo
        )
    }

    /**
     * Leave the current room.
     * Removes player from `fixed-rooms/{roomId}/players/{playerId}`.
     * Cleans up listeners.
     * 
     * Requirements: 1.5
     */
    fun leaveRoom(callback: (() -> Unit)? = null) {
        val roomId = currentRoomId
        val playerId = currentPlayerId
        
        // Stop heartbeat and cleanup listeners
        stopHeartbeat()
        unsubscribeFromRoom()
        
        // Remove player from Firebase and check if room is now empty
        if (roomId != null && playerId != null) {
            val roomRef = database?.ref("fixed-rooms/$roomId")
            
            // First remove the player
            database?.ref("fixed-rooms/$roomId/players/$playerId")?.remove(fun(_: dynamic): dynamic {
                console.log("Player $playerId removed from room $roomId")
                
                // Then check if room is now empty and reset gameStarted if so
                roomRef?.child("players")?.once("value", fun(snapshot: dynamic): dynamic {
                    val playersData = snapshot.`val`()
                    val isEmpty = playersData == null || js("Object.keys")(playersData).length == 0
                    
                    if (isEmpty) {
                        console.log("🧹 ROOM CLEANUP: Room $roomId is now empty, resetting gameStarted=false")
                        val updates = json(
                            "gameStarted" to false,
                            "gameInfo" to null,
                            "lastActivity" to Date.now()  // Use raw JS number, not toLong()
                        )
                        roomRef.update(updates, fun(error: dynamic): dynamic {
                            if (error != null) {
                                console.error("Failed to reset room state: ${error.message}")
                            } else {
                                console.log("✅ Room $roomId reset to empty state")
                            }
                            callback?.invoke()
                            return Unit.asDynamic()
                        })
                    } else {
                        console.log("Room $roomId still has players, not resetting")
                        callback?.invoke()
                    }
                    return Unit.asDynamic()
                })
                return Unit.asDynamic()
            })
        } else {
            callback?.invoke()
        }
        
        // Reset local state
        currentRoomId = null
        currentPlayerId = null
        currentPlayerName = null
        isHost = false
    }

    /**
     * Start the game (host only).
     * Clears any stale game state from previous games, then generates boardSeed 
     * and writes gameStarted and gameInfo to Firebase.
     * Also broadcasts game-started event via Pusher for Android clients.
     * 
     * Requirements: 6.3
     */
    fun startGame(callback: ((Boolean, String?) -> Unit)? = null) {
        if (isThirdPartyIframe || co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.getEnvironment() == co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.DISCORD) {
            startGameViaRest(callback)
            return
        }

        if (!isHost) {
            callback?.invoke(false, "Only the host can start the game")
            return
        }
        
        val roomId = currentRoomId
        if (roomId == null) {
            callback?.invoke(false, "Not in a room")
            return
        }
        
        console.log("🎮 Host starting game - clearing stale state first...")
        
        // CRITICAL: Clear any stale game state from previous games BEFORE starting
        // This prevents old moves/state from being loaded when joining an "empty" room
        clearStaleGameState(roomId) {
            // Generate board seed using current timestamp
            // Use raw JS number for Firebase compatibility (not Kotlin Long which becomes {u7_1, v7_1})
            val seed = Date.now()
            
            // Create game info - use raw JS numbers for Android compatibility
            val gameInfo = json(
                "gameId" to "game_${roomId}_${seed.toLong()}",
                "boardSeed" to seed,
                "currentPlayerId" to currentPlayerId,
                "startedAt" to seed
            )
            
            // Update room with game started state
            val updates = json(
                "gameStarted" to true,
                "gameInfo" to gameInfo,
                "lastActivity" to seed
            )
            
            database.ref("fixed-rooms/$roomId").update(updates, fun(error: dynamic): dynamic {
                if (error != null) {
                    callback?.invoke(false, "Failed to start game: ${error.message}")
                } else {
                    console.log("✅ Game started with fresh state")
                    // Also broadcast game-started via Pusher for Android clients
                    broadcastGameStartedViaPusher(roomId, gameInfo)
                    callback?.invoke(true, null)
                }
                return Unit.asDynamic()
            })
        }
    }

    private fun startGameViaRest(callback: ((Boolean, String?) -> Unit)? = null) {
        val roomId = currentRoomId ?: return
        console.log("🌐 Sentinel: Starting game for $roomId via REST")
        
        getAuthToken { token ->
            val authParam = if (token != null) "?auth=$token" else ""
            
            // 1. Clear events (REST DELETE)
            val isDiscord = co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.getEnvironment() == co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.DISCORD
            val baseUrl = if (isDiscord) "/firebasedatabase" else "https://pinpoint-1978d-default-rtdb.europe-west1.firebasedatabase.app"
            val eventsUrl = "${baseUrl}/game-events/$roomId.json$authParam"
            window.fetch(eventsUrl, json("method" to "DELETE").asDynamic()).then {
                
                // 2. Clear SharedBoard data (REST DELETE)
            val gamesUrl = "${baseUrl}/games/$roomId.json$authParam"
                window.fetch(gamesUrl, json("method" to "DELETE").asDynamic()).then {
                    
                    // 3. Prepare game data
                    val seed = Date.now().toDouble()
                    val gameInfo = json(
                        "gameId" to "game_${roomId}_${seed.toLong()}",
                        "boardSeed" to seed,
                        "currentPlayerId" to currentPlayerId,
                        "startedAt" to seed
                    )
                    
                    val updates = json(
                        "gameStarted" to true,
                        "gameInfo" to gameInfo,
                        "lastActivity" to seed
                    )
                    
                    // 4. Update room (REST PATCH)
            val roomUrl = "${baseUrl}/fixed-rooms/$roomId.json$authParam"
                    window.fetch(roomUrl, json(
                        "method" to "PATCH",
                        "body" to JSON.stringify(updates)
                    ).asDynamic()).then { response ->
                        if (response.ok) {
                            console.log("✅ Sentinel: Game started via REST")
                            callback?.invoke(true, null)
                        } else {
                            callback?.invoke(false, "Failed to start: HTTP ${response.status}")
                        }
                    }.catch { err ->
                        console.error("❌ Sentinel: Game start failed: ${err.message}")
                        callback?.invoke(false, err.message)
                    }
                }
            }
        }
    }
    
    /**
     * Clear stale game state from a previous game session.
     * This removes old game events from Firebase to prevent them from being replayed.
     * Called by host before starting a new game.
     */
    private fun clearStaleGameState(roomId: String, onComplete: () -> Unit) {
        console.log("🧹 Clearing stale game state for room $roomId")
        
        // Clear game events (moves, undo events, state updates from previous games)
        database.ref("game-events/$roomId").remove(fun(error: dynamic): dynamic {
            if (error != null) {
                console.error("Failed to clear game events: ${error.message}")
            } else {
                console.log("✅ Cleared game-events/$roomId")
            }
            
            // Also reset the gameInfo to null first (will be set with new values immediately after)
            // This ensures any listeners see a clean state transition
            database.ref("fixed-rooms/$roomId/gameInfo").remove(fun(error2: dynamic): dynamic {
                if (error2 != null) {
                    console.error("Failed to clear gameInfo: ${error2.message}")
                } else {
                    console.log("✅ Cleared fixed-rooms/$roomId/gameInfo")
                }
                onComplete()
                return Unit.asDynamic()
            })
            
            return Unit.asDynamic()
        })
    }
    
    /**
     * Broadcast game-started event via Pusher server.
     * This ensures Android clients receive the game started signal.
     */
    private fun broadcastGameStartedViaPusher(roomId: String, gameInfo: dynamic) {
        // Use localhost for local testing, production URL for deployed version
        val hostname = kotlinx.browser.window.location.hostname
        val serverUrl = if (hostname == "localhost" || hostname == "127.0.0.1") {
            "//localhost:8080/room/game-started"
        } else {
            "${kotlinx.browser.window.location.origin}/room/game-started"
        }
        
        val requestBody = json(
            "roomId" to roomId,
            "gameInfo" to gameInfo
        )
        
        val headers = js("{}")
        headers["Content-Type"] = "application/json"
        
        val fetchOptions = js("{}")
        fetchOptions["method"] = "POST"
        fetchOptions["headers"] = headers
        fetchOptions["body"] = JSON.stringify(requestBody)
        
        console.log("📡 Broadcasting game-started via Pusher")
        
        window.fetch(serverUrl, fetchOptions)
            .then { response ->
                if (response.ok) {
                    console.log("✅ game-started broadcast via Pusher successful")
                } else {
                    console.error("❌ game-started Pusher broadcast failed: ${response.status}")
                }
                Unit.asDynamic()
            }
            .catch { error ->
                console.error("❌ game-started Pusher broadcast error: $error")
                Unit.asDynamic()
            }
    }


    /**
     * Get the current date ID (e.g., "2025-12-24")
     */
    private fun getCurrentDateId(): String {
        val now = Date()
        val year = now.getFullYear()
        val month = (now.getMonth() + 1).toString().padStart(2, '0')
        val day = now.getDate().toString().padStart(2, '0')
        return "$year-$month-$day"
    }

    /**
     * Get the current week ID (e.g., "2025-W01")
     * Matches Android's Calendar.WEEK_OF_YEAR + Calendar.YEAR behavior exactly
     * 
     * Android uses US locale where:
     * - First day of week = Sunday
     * - Minimal days in first week = 1
     * - Week 1 is the week containing January 1st
     * 
     * This means Dec 28-31 can be week 1 if they're in the same week as Jan 1 of next year
     */
    private fun getCurrentWeekId(): String {
        val now = Date()
        val year = now.getFullYear()
        val month = now.getMonth()
        val dayOfMonth = now.getDate()
        val dayOfWeek = now.getDay() // 0=Sunday
        
        // Check if we're in the last few days of December that might be week 1
        if (month == 11 && dayOfMonth >= 28) {
            // Check if Jan 1 of next year is in the same week
            val nextYearJan1 = Date(year + 1, 0, 1)
            val jan1DayOfWeek = nextYearJan1.getDay() // 0=Sunday
            
            // If Jan 1 is not Sunday, then some days of December are in week 1
            // The week starts on Sunday, so Dec days from (Jan1 - jan1DayOfWeek) to Dec 31 are in week 1
            if (jan1DayOfWeek > 0) {
                val firstDayOfWeek1 = 32 - jan1DayOfWeek // Day of December when week 1 starts
                if (dayOfMonth >= firstDayOfWeek1) {
                    // We're in week 1, but Android uses Calendar.YEAR which is still current year
                    console.log("🗓️ Week calculation: Dec $dayOfMonth is in week 1 (starts Dec $firstDayOfWeek1), using $year-W01")
                    return "$year-W01"
                }
            }
        }
        
        // Check if we're in the first few days of January that might be week 52/53 of previous year
        if (month == 0 && dayOfMonth <= 3) {
            // If Jan 1 is not Sunday, the first few days might be in last year's last week
            val jan1DayOfWeek = Date(year, 0, 1).getDay()
            if (jan1DayOfWeek > 0 && dayOfWeek < jan1DayOfWeek) {
                // We're before the first Sunday of the year, so we're in last year's last week
                // But Android uses Calendar.YEAR which is current year, and WEEK_OF_YEAR which wraps
                // Actually, Android would show this as week 1 since minimal days = 1
            }
        }
        
        // Standard week calculation
        val startOfYear = Date(year, 0, 1)
        val diffMs = now.getTime() - startOfYear.getTime()
        val dayOfYear = kotlin.math.floor(diffMs / 86400000.0).toInt() + 1
        val jan1DayOfWeek = startOfYear.getDay()
        
        // Week number: add jan1's day of week to shift, divide by 7, add 1
        val weekNo = ((dayOfYear + jan1DayOfWeek - 1) / 7) + 1
        
        console.log("🗓️ Week calculation: $year-${month+1}-$dayOfMonth, dayOfYear=$dayOfYear, weekNo=$weekNo")
        
        return "$year-W${weekNo.toString().padStart(2, '0')}"
    }
    
    private fun isLeapYear(year: Int): Boolean {
        return (year % 4 == 0 && year % 100 != 0) || (year % 400 == 0)
    }

    fun fetchLeaderboard(period: String, callback: (List<LeaderboardEntry>) -> Unit) {
        val path = when (period) {
            "dailyBest" -> "leaderboards/dailyBest/${getCurrentDateId()}"
            "allTimeBest" -> "leaderboards/allTimeBest"
            "weekly" -> "leaderboards/weekly/${getCurrentWeekId()}"
            "allTime" -> "leaderboards/allTime"
            else -> "leaderboards/allTime"
        }
        
        console.log("📊 Fetching leaderboard: period=$period, path=$path")
        
        val orderByField = if (period == "dailyBest" || period == "allTimeBest") "bestScore" else "totalScore"
        
        // Safely check environment
        val env = try {
            co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.getEnvironment()
        } catch (e: Exception) {
            console.warn("PlatformEnvironment not detected yet, using default")
            co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.STANDALONE
        }
        
        console.log("📊 Environment: $env, database available: ${database != null}")
        
        if (env == co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.DISCORD) {
            fetchLeaderboardViaRest(path, orderByField, callback)
            return
        }
        
        // For non-Discord platforms, use Firebase
        if (database == null) {
            console.warn("📊 Firebase not initialized, returning empty leaderboard")
            callback(emptyList())
            return
        }
        
        console.log("📊 Using Firebase SDK to fetch leaderboard from: $path")
        
        // Add timeout fallback
        var callbackCalled = false
        window.setTimeout({
            if (!callbackCalled) {
                console.error("📊 Leaderboard fetch timeout after 10 seconds")
                callbackCalled = true
                callback(emptyList())
            }
        }, 10000)
        
        try {
            val ref = database.ref(path)
            
            ref.once("value", fun(snapshot: dynamic): dynamic {
                if (callbackCalled) {
                    console.warn("📊 Leaderboard response received after timeout")
                    return Unit.asDynamic()
                }
                callbackCalled = true
                console.log("📊 Leaderboard snapshot received, processing...")
                val snapshotVal = snapshot.`val`()
                if (snapshotVal == null) {
                    console.log("📊 No data in leaderboard")
                    callback(emptyList())
                    return Unit.asDynamic()
                }
                
                val entries = mutableListOf<LeaderboardEntry>()
                
                // Manual iteration instead of forEach (forEach doesn't work well with dynamic objects)
                val keys = js("Object.keys")(snapshotVal) as Array<String>
                console.log("📊 Found ${keys.size} keys in snapshot")
                for (playerId in keys) {
                    val childSnapshot = snapshot.child(playerId)
                    val data = childSnapshot.`val`()
                    console.log("📊 Entry: playerId=$playerId, displayName=${data?.displayName}, bestScore=${data?.bestScore}, totalScore=${data?.totalScore}")
                    val entry = parseLeaderboardEntry(playerId, data)
                    if (entry != null) {
                        entries.add(entry)
                        console.log("✅ Added entry: $playerId")
                    }
                }
                
                console.log("📊 Parsed ${entries.size} leaderboard entries")
                // Firebase returns ascending, we want descending
                val sortedEntries = entries.sortedByDescending { it.totalScore }
                callback(sortedEntries)
                return Unit.asDynamic()
            }, fun(error: dynamic): dynamic {
                if (callbackCalled) return Unit.asDynamic()
                callbackCalled = true
                console.error("📊 Leaderboard fetch error: ${error.message}")
                callback(emptyList())
                return Unit.asDynamic()
            })
        } catch (e: Exception) {
            if (!callbackCalled) {
                callbackCalled = true
                console.error("📊 Exception fetching leaderboard: ${e.message}")
                callback(emptyList())
            }
        }
    }

    private fun fetchLeaderboardViaRest(path: String, orderBy: String, callback: (List<LeaderboardEntry>) -> Unit) {
        getAuthToken { token ->
            val authParam = if (token != null) "auth=$token" else ""
            val queryParams = if (authParam.isNotEmpty()) "$authParam&orderBy=\"$orderBy\"&limitToLast=50" else "orderBy=\"$orderBy\"&limitToLast=50"
            val proxyUrl = "/firebasedatabase/${path}.json?$queryParams"
            
            window.fetch(proxyUrl).then { response ->
                if (response.ok) {
                    response.json().then { data: dynamic ->
                        processLeaderboardData(data, callback)
                    }
                } else {
                    console.warn("⚠️ Sentinel: REST Leaderboard Error ${response.status}, trying fallback...")
                    val fallbackUrl = "/firebasedatabase/${path}.json?${if (authParam.isNotEmpty()) authParam else ""}"
                    window.fetch(fallbackUrl).then { fbRes ->
                        if (fbRes.ok) {
                            fbRes.json().then { data: dynamic ->
                                processLeaderboardData(data, callback)
                            }
                        } else {
                            callback(emptyList())
                        }
                    }
                }
            }.catch { err ->
                console.error("❌ Leaderboard REST error: ${err.message}")
                callback(emptyList())
            }
        }
    }

    private fun processLeaderboardData(data: dynamic, callback: (List<LeaderboardEntry>) -> Unit) {
        val entries = mutableListOf<LeaderboardEntry>()
        if (data != null) {
            val keys = js("Object.keys")(data) as Array<String>
            keys.forEach { playerId ->
                val entryData = data[playerId]
                val entry = parseLeaderboardEntry(playerId, entryData)
                if (entry != null) entries.add(entry)
            }
        }
        
        val sortedEntries = entries.sortedByDescending { it.totalScore }.take(50)
        callback(sortedEntries)
    }

    private fun parseLeaderboardEntry(playerId: String?, data: dynamic): LeaderboardEntry? {
        if (playerId == null || data == null) return null
        
        // Handle both LeaderboardEntry (weekly/allTime) and BestScoreEntry (dailyBest) schemas
        val totalScore = (data.totalScore as? Number)?.toInt() 
            ?: (data.bestScore as? Number)?.toInt() // Map bestScore to totalScore for display
            ?: 0
            
        val highestScore = (data.highestScore as? Number)?.toInt()
            ?: (data.bestScore as? Number)?.toInt()
            ?: 0
            
        val timestamp = (data.lastUpdated as? Number)?.toLong()
            ?: (data.timestamp as? Number)?.toLong()
            ?: 0L
            
        return LeaderboardEntry(
            playerId = playerId,
            displayName = data.displayName as? String ?: "Unknown",
            totalScore = totalScore,
            gamesPlayed = (data.gamesPlayed as? Number)?.toInt() ?: 0,
            wins = (data.wins as? Number)?.toInt() ?: 0,
            highestScore = highestScore,
            lastUpdated = timestamp
        )
    }

    private fun startHeartbeat(roomId: String, playerId: String) {
        stopHeartbeat()
        heartbeatIntervalId = window.setInterval({
            if (currentRoomId == roomId && currentPlayerId == playerId) {
                val currentTime = Date.now()  // Use raw JS number, not toLong()
                database.ref("fixed-rooms/$roomId/players/$playerId/lastActivity").set(currentTime)
                // Also update room's overall activity
                database.ref("fixed-rooms/$roomId/lastActivity").set(currentTime)
                database.ref("fixed-rooms/$roomId/lastActivity").set(currentTime)
            } else {
                stopHeartbeat()
            }
        }, 30000) // Every 30 seconds
    }

    private fun stopHeartbeat() {
        heartbeatIntervalId?.let { window.clearInterval(it) }
        heartbeatIntervalId = null
    }

    fun stopAll() {
        stopHeartbeat()
        unsubscribeFromRoom()
        this.currentRoomId = null
        this.currentPlayerId = null
        this.currentPlayerName = null
        this.isHost = false
    }
    
    // ==========================================
    // AI Player Management
    // ==========================================
    // Leaderboard Score Submission
    // ==========================================
    
    /**
     * Submit a score to the leaderboard after a multiplayer game.
     * Updates daily, weekly, and all-time leaderboards.
     */
    fun submitScore(playerId: String, displayName: String, score: Int, isWin: Boolean, callback: (Boolean) -> Unit) {
        val db = database
        if (db == null) {
            console.error("FirebaseRoomManager: Cannot submit score - database not initialized")
            callback(false)
            return
        }
        
        val timestamp = Date.now().toLong()
        val dateId = getCurrentDateId()
        val weekId = getCurrentWeekId()
        
        console.log("📊 Submitting score: playerId=$playerId, displayName=$displayName, score=$score, win=$isWin, date=$dateId, week=$weekId")
        
        // Update daily best score
        val dailyRef = db.ref("leaderboards/dailyBest/$dateId/$playerId")
        dailyRef.once("value").then { snapshot: dynamic ->
            val existingData = snapshot.`val`()
            val existingBest = (existingData?.bestScore as? Number)?.toInt() ?: 0
            
            if (score > existingBest) {
                dailyRef.set(json(
                    "displayName" to displayName,
                    "bestScore" to score,
                    "timestamp" to timestamp
                ))
                console.log("✅ Updated daily best: $score")
            }
        }
        
        // Update all-time best score
        val allTimeBestRef = db.ref("leaderboards/allTimeBest/$playerId")
        allTimeBestRef.once("value").then { snapshot: dynamic ->
            val existingData = snapshot.`val`()
            val existingBest = (existingData?.bestScore as? Number)?.toInt() ?: 0
            
            if (score > existingBest) {
                allTimeBestRef.set(json(
                    "displayName" to displayName,
                    "bestScore" to score,
                    "timestamp" to timestamp
                ))
                console.log("✅ Updated all-time best: $score")
            }
        }
        
        // Update weekly leaderboard
        val weeklyRef = db.ref("leaderboards/weekly/$weekId/$playerId")
        weeklyRef.once("value").then { snapshot: dynamic ->
            val existingData = snapshot.`val`()
            val existingTotal = (existingData?.totalScore as? Number)?.toInt() ?: 0
            val existingGames = (existingData?.gamesPlayed as? Number)?.toInt() ?: 0
            val existingWins = (existingData?.wins as? Number)?.toInt() ?: 0
            val existingHighest = (existingData?.highestScore as? Number)?.toInt() ?: 0
            
            weeklyRef.set(json(
                "displayName" to displayName,
                "totalScore" to (existingTotal + score),
                "gamesPlayed" to (existingGames + 1),
                "wins" to (existingWins + if (isWin) 1 else 0),
                "highestScore" to maxOf(existingHighest, score),
                "timestamp" to timestamp
            ))
            console.log("✅ Updated weekly leaderboard")
        }
        
        // Update all-time leaderboard
        val allTimeRef = db.ref("leaderboards/allTime/$playerId")
        allTimeRef.once("value").then { snapshot: dynamic ->
            val existingData = snapshot.`val`()
            val existingTotal = (existingData?.totalScore as? Number)?.toInt() ?: 0
            val existingGames = (existingData?.gamesPlayed as? Number)?.toInt() ?: 0
            val existingWins = (existingData?.wins as? Number)?.toInt() ?: 0
            val existingHighest = (existingData?.highestScore as? Number)?.toInt() ?: 0
            
            allTimeRef.set(json(
                "displayName" to displayName,
                "totalScore" to (existingTotal + score),
                "gamesPlayed" to (existingGames + 1),
                "wins" to (existingWins + if (isWin) 1 else 0),
                "highestScore" to maxOf(existingHighest, score),
                "timestamp" to timestamp
            ))
            console.log("✅ Updated all-time leaderboard")
            callback(true)
        }.catch { error: dynamic ->
            console.error("❌ Failed to submit score: ${error.message}")
            callback(false)
        }
    }
    
    // ==========================================
    // AI Player Management
    // ==========================================
    
    /**
     * Add an AI player to the current room (host only).
     * Creates an AI player with a unique ID and adds it to the room.
     */
    fun addAIPlayer(difficulty: String, callback: (Boolean, String?) -> Unit) {
        if (isThirdPartyIframe || co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.getEnvironment() == co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.DISCORD) {
            addAIPlayerViaRest(difficulty, callback)
            return
        }

        val roomId = currentRoomId
        if (roomId == null) {
            callback(false, "Not in a room")
            return
        }
        
        if (!isHost) {
            callback(false, "Only the host can add AI players")
            return
        }
        
        if (database == null) {
            callback(false, "Not connected to Firebase")
            return
        }
        
        // First check room state to ensure there's space
        database.ref("fixed-rooms/$roomId").once("value", fun(snapshot: dynamic): dynamic {
            val roomData = snapshot.`val`()
            if (roomData == null) {
                callback(false, "Room not found")
                return Unit.asDynamic()
            }
            
            val players = roomData.players ?: js("{}")
            val playerKeys = js("Object.keys")(players) as Array<String>
            val maxPlayers = (roomData.maxPlayers as? Int) ?: 4
            
            if (playerKeys.size >= maxPlayers) {
                callback(false, "Room is full")
                return Unit.asDynamic()
            }
            
            // Generate unique AI player ID and name - match Android pattern
            // Find next available AI number (1, 2, 3, etc.)
            val existingAINumbers = playerKeys
                .filter { key -> 
                    val player = players[key]
                    val isAIPlayer = (player?.ai as? Boolean) == true || (player?.isAI as? Boolean) == true
                    isAIPlayer
                }
                .mapNotNull { it.removePrefix("ai_").toIntOrNull() }
            
            val nextAINumber = (1..10).first { it !in existingAINumbers }
            val aiId = "ai_$nextAINumber"
            val aiName = generateAIName(difficulty)
            
            // Create AI player info - match Android PlayerInfo structure
            val currentTime = Date.now()
            val aiPlayerInfo = json(
                "id" to aiId,
                "name" to aiName,
                "host" to false, // Matches Android lobby field
                "connected" to true,
                "joinedAt" to currentTime,
                "guest" to false,
                "ai" to true, // Matches Android lobby field
                "aiDifficulty" to difficulty,
                "lastActivity" to currentTime
            )
            
            // Add AI player to Firebase
            val aiPlayerRef = database.ref("fixed-rooms/$roomId/players/$aiId")
            aiPlayerRef.set(aiPlayerInfo, fun(error: dynamic): dynamic {
                if (error != null) {
                    callback(false, "Failed to add AI player: ${error.message}")
                } else {
                    console.log("✅ Added AI player: $aiName ($difficulty)")
                    callback(true, null)
                    // Immediately refresh room state to reflect AI addition
                    startRoomStatePollingViaRest(roomId)
                }
                return Unit.asDynamic()
            })
            
            return Unit.asDynamic()
        })
    }

    private fun addAIPlayerViaRest(difficulty: String, callback: (Boolean, String?) -> Unit) {
        val roomId = currentRoomId ?: return
        getAuthToken { token ->
            val authParam = if (token != null) "?auth=$token" else ""
            val isDiscord = co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.getEnvironment() == co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.DISCORD
            val baseUrl = if (isDiscord) "/firebasedatabase" else "https://pinpoint-1978d-default-rtdb.europe-west1.firebasedatabase.app"
            val url = "${baseUrl}/fixed-rooms/$roomId.json$authParam"
            
            window.fetch(url).then { response ->
                if (response.ok) {
                    response.json().then { roomData: dynamic ->
                        if (roomData == null) {
                            callback(false, "Room not found")
                            return@then
                        }
                        
                        val players = roomData.players ?: js("{}")
                        val playerKeys = js("Object.keys")(players) as Array<String>
                        val maxPlayers = (roomData.maxPlayers as? Int) ?: 4
                        
                        if (playerKeys.size >= maxPlayers) {
                            callback(false, "Room is full")
                            return@then
                        }
                        
                        // Find next available AI number
                        val existingAINumbers = playerKeys
                            .filter { key -> 
                                val player = players[key]
                                val isAIPlayer = (player?.ai as? Boolean) == true || (player?.isAI as? Boolean) == true
                                isAIPlayer
                            }
                            .mapNotNull { it.removePrefix("ai_").toIntOrNull() }
                        
                        val nextAINumber = (1..10).first { it !in existingAINumbers }
                        val aiId = "ai_$nextAINumber"
                        val aiName = generateAIName(difficulty)
                        
                        val currentTime = Date.now()
                        val aiPlayerInfo = json(
                            "id" to aiId,
                            "name" to aiName,
                            "host" to false,
                            "connected" to true,
                            "joinedAt" to currentTime,
                            "guest" to false,
                            "ai" to true,
                            "aiDifficulty" to difficulty,
                            "lastActivity" to currentTime
                        )
                        
                        // Add via PUT
                        val updateUrl = "${baseUrl}/fixed-rooms/$roomId/players/$aiId.json$authParam"
                        window.fetch(updateUrl, json(
                            "method" to "PUT",
                            "body" to JSON.stringify(aiPlayerInfo)
                        ).asDynamic()).then { patchResponse ->
                            if (patchResponse.ok) {
                                console.log("✅ Sentinel: AI added via REST")
                                callback(true, null)
                            } else {
                                callback(false, "Failed to add AI: HTTP ${patchResponse.status}")
                            }
                        }
                    }
                } else {
                    callback(false, "Failed to fetch room state: HTTP ${response.status}")
                }
            }
        }
    }

    private fun removeAIPlayerViaRest(playerId: String, callback: (Boolean, String?) -> Unit) {
        val roomId = currentRoomId ?: return
        getAuthToken { token ->
            val authParam = if (token != null) "?auth=$token" else ""
            val isDiscord = co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.getEnvironment() == co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.DISCORD
            val baseUrl = if (isDiscord) "/firebasedatabase" else "https://pinpoint-1978d-default-rtdb.europe-west1.firebasedatabase.app"
            val url = "${baseUrl}/fixed-rooms/$roomId/players/$playerId.json$authParam"
            
            window.fetch(url, json("method" to "DELETE").asDynamic()).then { response ->
                if (response.ok) {
                    console.log("✅ Sentinel: AI removed via REST")
                    callback(true, null)
                } else {
                    callback(false, "Failed to remove AI: HTTP ${response.status}")
                }
            }.catch { err ->
                callback(false, err.message)
            }
        }
    }
    
    /**
     * Remove an AI player from the current room (host only).
     */
    fun removeAIPlayer(playerId: String, callback: (Boolean, String?) -> Unit) {
        if (isThirdPartyIframe || co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.getEnvironment() == co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.DISCORD) {
            removeAIPlayerViaRest(playerId, callback)
            return
        }

        val roomId = currentRoomId
        if (roomId == null) {
            callback(false, "Not in a room")
            return
        }
        
        if (!isHost) {
            callback(false, "Only the host can remove AI players")
            return
        }
        
        if (database == null) {
            callback(false, "Not connected to Firebase")
            return
        }
        
        // Verify the player is an AI before removing
        database.ref("fixed-rooms/$roomId/players/$playerId").once("value", fun(snapshot: dynamic): dynamic {
            val playerData = snapshot.`val`()
            if (playerData == null) {
                callback(false, "Player not found")
                return Unit.asDynamic()
            }
            
            console.log("FirebaseRoomManager: Player data for $playerId:", playerData)
            console.log("FirebaseRoomManager: isAI property:", playerData.isAI)
            console.log("FirebaseRoomManager: ai property:", playerData.ai)
            console.log("FirebaseRoomManager: isAI type:", js("typeof playerData.isAI"))
            console.log("FirebaseRoomManager: ai type:", js("typeof playerData.ai"))
            
            // Check both 'ai' and 'isAI' properties (web uses 'ai', some data might use 'isAI')
            val isAI = (playerData.isAI as? Boolean) == true || (playerData.ai as? Boolean) == true
            console.log("FirebaseRoomManager: Combined isAI check:", isAI)
            
            if (!isAI) {
                console.log("FirebaseRoomManager: AI check failed - not an AI player")
                callback(false, "Can only remove AI players")
                return Unit.asDynamic()
            }
            
            // Remove the AI player
            database.ref("fixed-rooms/$roomId/players/$playerId").remove(fun(error: dynamic): dynamic {
                if (error != null) {
                    callback(false, "Failed to remove AI player: ${error.message}")
                } else {
                    console.log("✅ Removed AI player: $playerId")
                    callback(true, null)
                }
                return Unit.asDynamic()
            })
            
            return Unit.asDynamic()
        })
    }
    
    /**
     * Generate a name for an AI player based on difficulty.
     * Uses names from Star Trek: The Next Generation for consistency with Android app.
     */
    private fun generateAIName(difficulty: String): String {
        // Android hardcodes the name to "Data" (see PlayerInfo.createAIPlayer in Android)
        // We match this behavior for consistency, even though random names are more fun.
        return "Data"
    }

    private var authPromise: dynamic = null
    private var lastAuthTime: Double = 0.0

    fun getAuthToken(callback: (String?) -> Unit) {
        val now = kotlinx.browser.window.asDynamic().Date.now() as Double
        // Cache for 50 minutes (3,000,000 ms) instead of 5
        if (authPromise != null && (now - lastAuthTime) < 3000000.0) {
            authPromise.then(fun(data: dynamic): dynamic {
                callback(data.idToken as? String)
                return Unit.asDynamic()
            })
            return
        }

        lastAuthTime = now
        authPromise = js("""new Promise(function(resolve, reject) {
            function performRestAuth() {
                var apiKey = window.firebaseConfig ? window.firebaseConfig.apiKey : "";
                if (!apiKey) apiKey = "AIzaSyAuSwlSwnP7g7qQb2UIdKdpX9tE5sxkE50"; // Fallback to constant
                
                console.log("🔐 getAuthToken: Attempting REST auth...");
                var url = "https://identitytoolkit.googleapis.com/v1/accounts:signUp?key=" + apiKey;
                window.fetch(url, { 
                    method: 'POST', 
                    body: '{}', 
                    headers: { 'Content-Type': 'application/json' } 
                }).then(function(response) {
                    if (response.ok) {
                        response.json().then(function(data) {
                            console.log("🔐 getAuthToken: REST auth success");
                            resolve({ idToken: data.idToken, localId: data.localId });
                        });
                    } else {
                        console.error("❌ getAuthToken: REST auth failed with status " + response.status);
                        resolve({ idToken: null, localId: null });
                    }
                }).catch(function(err) {
                    console.error("❌ getAuthToken: REST auth network error: " + err.message);
                    resolve({ idToken: null, localId: null });
                });
            }

            function checkAuth() {
                var user = null;
                try {
                    user = firebase.auth().currentUser;
                } catch(e) {}

                if (user) {
                    user.getIdToken().then(function(token) {
                        resolve({ idToken: token, localId: user.uid });
                    });
                } else {
                    performRestAuth();
                }
            }

            checkAuth();
        })""")
        
        authPromise.then(fun(data: dynamic): dynamic {
            callback(data.idToken as? String)
            return Unit.asDynamic()
        })
    }

    fun updatePlayerProfile(name: String, photoURL: String? = null) {
        currentPlayerName = name
        val roomId = currentRoomId ?: return
        val playerId = currentPlayerId ?: return
        
        console.log("👤 Updating player profile in room $roomId: $name")
        
        if (co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.getEnvironment() == co.uk.paxes.pinpoint.web.platform.PlatformEnvironment.Environment.DISCORD) {
            // Discord Mode: Update via REST bridge
            getAuthToken { token ->
                val authParam = if (token != null) "?auth=$token" else ""
                val profileUrl = "/firebasedatabase/fixed-rooms/$roomId/players/$playerId.json$authParam"
                
                val updates = json("name" to name)
                if (photoURL != null) updates["photoURL"] = photoURL
                
                val requestInit = json(
                    "method" to "PATCH",
                    "body" to JSON.stringify(updates)
                ).asDynamic().unsafeCast<org.w3c.fetch.RequestInit>()
                
                window.fetch(profileUrl, requestInit).then { 
                    console.log("✅ Profile updated via REST")
                    refreshLocalRoomState(playerId, name)
                }
            }
        } else {
            // Browser Mode: Update via Firebase SDK
            database.ref("fixed-rooms/$roomId/players/$playerId").update(json(
                "name" to name
            )).then {
                console.log("✅ Profile updated via SDK")
                refreshLocalRoomState(playerId, name)
            }
        }
        
        // Also update SharedBoardManager if we are in an active game
        SharedBoardManager.updatePlayer(playerId, mapOf("name" to name))
    }

    private fun refreshLocalRoomState(playerId: String, name: String) {
        val state = lastRoomState
        if (state != null) {
            val updatedPlayers = state.players.toMutableMap()
            val p = updatedPlayers[playerId]
            if (p != null) {
                updatedPlayers[playerId] = p.copy(name = name)
                onRoomStateChanged?.invoke(state.copy(players = updatedPlayers))
            }
        }
    }

    fun updateCurrentPlayerName(newName: String) {
        updatePlayerProfile(newName)
    }
}

data class LeaderboardEntry(
    val playerId: String,
    val displayName: String,
    val totalScore: Int,
    val gamesPlayed: Int,
    val wins: Int,
    val highestScore: Int,
    val lastUpdated: Long
)
